import React, { useRef, useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Line } from 'react-chartjs-2';
import { makeStyles, useTheme } from '@mui/styles';
import {
	Card, CardHeader, CardContent, Divider, Typography, IconButton, Tooltip
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import { ExportChartToPdf } from './exportChartPdf';
import { registerChart } from './chartRegistry';

/*
 * O projecto usa Chart.js 2 / react-chartjs-2 2.x (ver views/DashboardDefault).
 * A sintaxe das opcoes e a antiga: scales.xAxes/yAxes em array, legend e
 * tooltips ao nivel de topo. Nao mudar para a sintaxe do Chart.js 3 sem
 * actualizar a dependencia em todo o projecto.
 */

const FONT_FAMILY = "'Roboto', 'Helvetica', 'Arial', sans-serif";

/* Ids dos eixos Y. Em modo simples so existe o y1; em duplo eixo existe y1+y2. */
const Y1 = 'y1';
const Y2 = 'y2';

const useStyles = makeStyles((theme) => ({
	root: {
		height: '100%',
		display: 'flex',
		flexDirection: 'column'
	},
	header: {
		paddingBottom: theme.spacing(1)
	},
	title: {
		fontWeight: 600,
		letterSpacing: '-0.2px'
	},
	unit: {
		display: 'inline-block',
		marginTop: theme.spacing(0.5),
		padding: '2px 10px',
		borderRadius: 6,
		fontSize: 12,
		fontWeight: 600,
		lineHeight: 1.6,
		color: theme.palette.text.secondary,
		backgroundColor: (theme.palette.action && theme.palette.action.hover) || 'rgba(0, 0, 0, 0.04)'
	},
	headerAction: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1)
	},
	content: {
		flexGrow: 1,
		paddingTop: theme.spacing(2)
	},
	chart: {
		position: 'relative',
		height: 340,
		padding: theme.spacing(1)
	}
}));

/*
 * Paleta curada e coerente (tons Tableau/Tailwind), pensada para linhas que se
 * distinguem bem entre si sem parecerem "neon". O vermelho NAO entra aqui: fica
 * reservado para a marca do Work Day, para nao se confundir com uma serie.
 */
export const CHART_COLORS = [
	'#2563EB', // azul
	'#059669', // esmeralda
	'#D97706', // ambar
	'#7C3AED', // violeta
	'#0891B2', // cyan
	'#DB2777', // rosa
	'#65A30D', // lima
	'#4F46E5', // indigo
	'#EA580C', // laranja
	'#0D9488' // teal
];

const COLORS = CHART_COLORS;

const WORK_DAY_COLOR = '#DC2626';

/* Converte um hex em rgba, para preenchimentos de area subtis. */
const HexToRgba = (hex, alpha) => {
	const value = hex.replace('#', '');
	const r = parseInt(value.substring(0, 2), 16);
	const g = parseInt(value.substring(2, 4), 16);
	const b = parseInt(value.substring(4, 6), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const IsPercentage = (unit) => unit === '%';

/*
 * Casas decimais necessarias para as marcas do eixo Y serem DISTINTAS.
 *
 * Sem isto, um KPI de disponibilidade que anda nos 99.99%-100% dava um eixo
 * com todas as marcas iguais ("100%", "100%", ...) porque arredondavamos a 1
 * casa. Olhamos para o espacamento real entre marcas e escolhemos casas
 * suficientes para as distinguir.
 */
const TickDecimals = (values, fallback) => {
	if (!Array.isArray(values) || values.length < 2) {
		return fallback;
	}
	const nums = values
		.map(v => (v && typeof v === 'object' ? v.value : v))
		.filter(n => typeof n === 'number' && !Number.isNaN(n));
	let minGap = Infinity;
	for (let i = 1; i < nums.length; i += 1) {
		const gap = Math.abs(nums[i] - nums[i - 1]);
		if (gap > 0 && gap < minGap) {
			minGap = gap;
		}
	}
	if (!Number.isFinite(minGap)) {
		return fallback;
	}
	return Math.min(4, Math.max(0, Math.ceil(-Math.log10(minGap))));
};

/*
 * Formatacao das marcas de um eixo Y, para uma dada unidade. Usa casas decimais
 * adaptativas e, para numeros grandes que nao sejam %, notacao compacta (1.2K).
 */
const FormatAxis = (value, values, unit) => {
	if (value === null || value === undefined || Number.isNaN(value)) {
		return '';
	}
	const isPct = IsPercentage(unit);
	const decimals = TickDecimals(values, isPct ? 2 : 0);
	if (isPct) {
		return `${new Intl.NumberFormat('en', {
			minimumFractionDigits: decimals, maximumFractionDigits: decimals
		}).format(value)}%`;
	}
	const abs = Math.abs(value);
	if (abs >= 1000) {
		return new Intl.NumberFormat('en', {
			notation: 'compact', maximumFractionDigits: 1
		}).format(value);
	}
	return new Intl.NumberFormat('en', {
		minimumFractionDigits: decimals, maximumFractionDigits: decimals
	}).format(value);
};

const FormatTooltip = (value, unit) => {
	if (value === null || value === undefined || Number.isNaN(value)) {
		return '—';
	}
	if (IsPercentage(unit)) {
		/*
		 * KPIs de disponibilidade andam nos 99.99xx%: com 2 casas o tooltip
		 * arredondava tudo para "100%", que nao batia certo com o eixo. Damos ate
		 * 6 casas (minimo 0), por isso 100% continua "100%" mas 99.99966% aparece
		 * com o detalhe real.
		 */
		return `${new Intl.NumberFormat('en', {
			minimumFractionDigits: 0, maximumFractionDigits: 6
		}).format(value)}%`;
	}
	return new Intl.NumberFormat('en', { maximumFractionDigits: 2 }).format(value);
};

/*
 * Indice do bucket que corresponde ao Work Day escolhido.
 * Devolve -1 se nao houver Work Day ou se ele cair fora do intervalo pedido.
 */
const GetWorkDayIndex = (buckets, workDay) => {
	if (!workDay) {
		return -1;
	}
	const target = moment(workDay);
	if (!target.isValid()) {
		return -1;
	}
	return buckets.findIndex(bucket => moment(bucket).isSame(target, 'day'));
};

/*
 * Plugin inline que desenha a linha vertical tracejada do Work Day.
 *
 * O Chart.js 2 nao tem reference lines e o projecto nao tem o
 * chartjs-plugin-annotation instalado, por isso desenhamos directamente no
 * canvas, depois dos datasets. O indice vem em options.workDayIndex. Em duplo
 * eixo usamos o eixo esquerdo (y1) para os limites verticais.
 */
const WorkDayLinePlugin = {
	afterDatasetsDraw: (chart) => {
		const index = chart.options.workDayIndex;

		if (index === null || index === undefined || index < 0) {
			return;
		}

		// 'x-axis-0' e a nomenclatura do Chart.js 2; 'x'/'y1' o que usamos aqui
		const xScale = chart.scales['x-axis-0'] || chart.scales.x;
		const yScale = chart.scales[Y1] || chart.scales['y-axis-0'] || chart.scales.y;

		if (!xScale || !yScale || !xScale.getPixelForTick) {
			return;
		}

		const x = xScale.getPixelForTick(index);

		if (Number.isNaN(x)) {
			return;
		}

		const { ctx } = chart;

		ctx.save();
		ctx.beginPath();
		ctx.setLineDash([6, 4]);
		ctx.lineWidth = 1.5;
		ctx.strokeStyle = WORK_DAY_COLOR;
		ctx.moveTo(x, yScale.top);
		ctx.lineTo(x, yScale.bottom);
		ctx.stroke();

		// etiqueta com "pill" para nao se perder por cima das linhas
		ctx.setLineDash([]);
		ctx.font = `600 11px ${FONT_FAMILY}`;
		const label = 'Work Day';
		const textWidth = ctx.measureText(label).width;
		const paddingX = 6;
		const boxWidth = textWidth + paddingX * 2;
		const boxHeight = 18;
		const boxX = Math.min(
			Math.max(x - boxWidth / 2, yScale.left || 0),
			(xScale.right || x) - boxWidth
		);
		const boxY = yScale.top + 2;

		ctx.fillStyle = WORK_DAY_COLOR;
		if (ctx.roundRect) {
			ctx.beginPath();
			ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 4);
			ctx.fill();
		} else {
			ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
		}

		ctx.fillStyle = '#FFFFFF';
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.fillText(label, boxX + boxWidth / 2, boxY + boxHeight / 2 + 0.5);
		ctx.restore();
	}
};

/* Ordena as series de uma camada pelo rotulo, com comparacao numerica. */
const OrderSeries = (series) => [...series].sort((a, b) => (
	String(a.grouping).localeCompare(String(b.grouping), undefined, { numeric: true })
));

const KPIsOverviewChart = (props) => {
	const {
		className, kpi, series, kpi2, series2, buckets, granularity, workDay,
		shownGroupings, colorMap, chartKey, ...rest
	} = props;

	const classes = useStyles();
	const theme = useTheme();
	const containerRef = useRef(null);
	const entryRef = useRef(null);

	const isDual = !!(kpi2 && series2 && series2.length);
	const isHourly = granularity === 'hourly';
	const workDayIndex = GetWorkDayIndex(buckets, workDay);

	const unit1 = (kpi && kpi.unit) || '';
	const unit2 = (kpi2 && kpi2.unit) || '';

	/*
	 * Etiquetas do eixo mais curtas ('12 Aug' / '12 Aug 09:00'). O tooltip
	 * mostra a data completa.
	 */
	const axisFmt = isHourly ? 'DD MMM HH:mm' : 'DD MMM';
	const fullFmt = isHourly ? 'DD MMM YYYY, HH:mm' : 'DD MMM YYYY';
	const labels = buckets.map(bucket => moment(bucket).format(axisFmt));

	const orderedPrimary = OrderSeries(series);
	const orderedSecondary = isDual ? OrderSeries(series2) : [];

	/*
	 * Visibilidade partilhada entre TODOS os graficos (vem do componente pai).
	 * shownGroupings vazio = tudo visivel.
	 */
	/*
	 * Chave de identidade de uma serie. Em duplo eixo e AGREGACAO + KPI (para ter
	 * cor propria por combinacao); em grafico simples e so o site (grouping). Tem
	 * de bater certo com a chave usada em KPIsOverviewCharts (colorMap/legenda).
	 */
	const keyOf = (serie) => (isDual ? `${serie.grouping}::${serie.kpiId}` : serie.grouping);

	const isShown = (serie) => (
		!shownGroupings.length || shownGroupings.includes(keyOf(serie))
	);

	// Num KPI unico, so preenchemos a area quando ha uma unica serie (com varias,
	// as areas transparentes empilhadas viram uma sopa ilegivel). Em duplo eixo
	// nao ha areas nem tracejados: cada serie (agregacao + KPI) tem cor propria.
	const singleFill = !isDual && orderedPrimary.length === 1;

	const colorFor = (serie, index) => (
		(colorMap && colorMap[keyOf(serie)]) || COLORS[index % COLORS.length]
	);

	/*
	 * Constroi os datasets de uma camada (um KPI) para um eixo. Em duplo eixo o
	 * rotulo inclui o nome do KPI ("12385 · VoLTE SSR"), para a legenda e o
	 * tooltip identificarem a combinacao.
	 */
	const buildDatasets = (ordered, axisID, unit, kpiObj, filled) => ordered.map((serie, index) => {
		const color = colorFor(serie, index);
		return {
			label: isDual && kpiObj ? `${serie.grouping} · ${kpiObj.text}` : serie.grouping,
			data: serie.values,
			hidden: !isShown(serie),
			yAxisID: axisID,
			// campo proprio (Chart.js ignora) usado no tooltip para formatar por unidade
			unit,
			borderColor: color,
			backgroundColor: filled ? HexToRgba(color, 0.12) : 'transparent',
			fill: filled,
			pointBackgroundColor: color,
			pointBorderColor: '#FFFFFF',
			pointBorderWidth: 1.5,
			pointRadius: buckets.length > 60 ? 0 : 2.5,
			pointHoverRadius: 5,
			pointHoverBorderWidth: 2,
			borderWidth: 2,
			lineTension: 0.25,
			cubicInterpolationMode: 'monotone',
			spanGaps: false
		};
	});

	const datasets = [
		...buildDatasets(orderedPrimary, Y1, unit1, kpi, singleFill),
		...(isDual ? buildDatasets(orderedSecondary, Y2, unit2, kpi2, false) : [])
	];

	const data = { labels, datasets };

	const gridColor = (theme.palette.divider) || 'rgba(0, 0, 0, 0.08)';
	const tickColor = (theme.palette.text && theme.palette.text.secondary) || '#6B7280';

	/*
	 * Eixo Y. `unit` define a escala/formatacao; `label` e o titulo do eixo. Em
	 * duplo eixo o titulo passa a nomear o KPI (ex.: "VoLTE SSR (%)"), para se
	 * saber logo qual eixo pertence a que KPI, independentemente da cor.
	 */
	const makeYAxis = (id, unit, label, position, drawGrid) => ({
		id,
		position,
		scaleLabel: label ? {
			display: true,
			labelString: label,
			fontColor: tickColor,
			fontFamily: FONT_FAMILY,
			fontSize: 11
		} : { display: false },
		gridLines: {
			borderDash: [3, 3],
			color: gridColor,
			drawBorder: false,
			drawOnChartArea: drawGrid,
			zeroLineColor: gridColor
		},
		ticks: {
			fontColor: tickColor,
			fontSize: 11,
			fontFamily: FONT_FAMILY,
			padding: 8,
			maxTicksLimit: 6,
			// os KPIs em % nao comecam em zero, senao nao se ve nada entre 98 e 100
			beginAtZero: !IsPercentage(unit),
			callback: (value, index, values) => FormatAxis(value, values, unit)
		}
	});

	const axisLabel = (kpiObj, unit) => {
		const name = kpiObj ? kpiObj.text : '';
		if (name && unit) {
			return `${name} (${unit})`;
		}
		return name || unit;
	};

	// em duplo eixo o titulo nomeia o KPI; em grafico simples mantem-se so a unidade
	const y1Label = isDual ? axisLabel(kpi, unit1) : unit1;
	const yAxes = [makeYAxis(Y1, unit1, y1Label, 'left', true)];
	if (isDual) {
		// eixo direito: sem grelha propria para nao duplicar as linhas horizontais
		yAxes.push(makeYAxis(Y2, unit2, axisLabel(kpi2, unit2), 'right', false));
	}

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		animation: false,
		layout: {
			padding: { top: 8, right: 8, bottom: 0, left: 0 }
		},
		// lido pelo WorkDayLinePlugin
		workDayIndex,
		hover: {
			mode: 'index',
			intersect: false
		},
		/*
		 * Legenda interna DESLIGADA de proposito. Existe uma legenda lateral
		 * unica, partilhada por todos os KPIs, em KPIsOverviewCharts.
		 */
		legend: {
			display: false
		},
		scales: {
			xAxes: [{
				gridLines: {
					display: false,
					drawBorder: false
				},
				ticks: {
					fontColor: tickColor,
					fontSize: 11,
					fontFamily: FONT_FAMILY,
					padding: 8,
					maxTicksLimit: 8,
					autoSkip: true,
					maxRotation: 30,
					minRotation: 0
				}
			}],
			yAxes
		},
		tooltips: {
			enabled: true,
			mode: 'index',
			intersect: false,
			backgroundColor: 'rgba(17, 24, 39, 0.92)',
			titleFontColor: '#F9FAFB',
			titleFontStyle: 'bold',
			titleFontFamily: FONT_FAMILY,
			bodyFontColor: '#E5E7EB',
			bodyFontFamily: FONT_FAMILY,
			bodySpacing: 6,
			xPadding: 12,
			yPadding: 10,
			cornerRadius: 8,
			caretSize: 6,
			titleMarginBottom: 8,
			displayColors: true,
			boxWidth: 8,
			usePointStyle: true,
			callbacks: {
				title: (items) => {
					if (!items.length) {
						return '';
					}
					const bucket = buckets[items[0].index];
					return bucket ? moment(bucket).format(fullFmt) : items[0].xLabel;
				},
				label: (item, chartData) => {
					const dataset = chartData.datasets[item.datasetIndex];
					const name = dataset ? dataset.label : '';
					const unit = dataset ? dataset.unit : '';
					return `  ${name}: ${FormatTooltip(item.yLabel, unit)}`;
				}
			}
		}
	};

	/* --- Titulo, periodo e legenda para exportacao / cabecalho --- */
	const title = isDual
		? `${kpi ? kpi.text : ''} vs ${kpi2 ? kpi2.text : ''}`
		: (kpi ? kpi.text : '');

	const periodStart = buckets.length ? moment(buckets[0]).format('DD MMM YYYY') : '';
	const periodEnd = buckets.length ? moment(buckets[buckets.length - 1]).format('DD MMM YYYY') : '';
	const periodLabel = periodStart && periodEnd ? `${periodStart} — ${periodEnd}` : '';
	const subtitle = [periodLabel, granularity].filter(Boolean).join('  ·  ');
	const periodForFile = buckets.length
		? `${moment(buckets[0]).format('YYYYMMDD')}-${moment(buckets[buckets.length - 1]).format('YYYYMMDD')}`
		: '';

	/* Entradas de legenda (apenas series visiveis). */
	const buildLegend = () => {
		const entries = [];
		orderedPrimary.forEach((serie, index) => {
			if (isShown(serie)) {
				entries.push({
					label: isDual && kpi ? `${serie.grouping} · ${kpi.text}` : String(serie.grouping),
					color: colorFor(serie, index),
					dashed: false
				});
			}
		});
		if (isDual) {
			orderedSecondary.forEach((serie, index) => {
				if (isShown(serie)) {
					entries.push({
						label: kpi2 ? `${serie.grouping} · ${kpi2.text}` : String(serie.grouping),
						color: colorFor(serie, index),
						dashed: false
					});
				}
			});
		}
		return entries;
	};

	/*
	 * Vamos buscar o <canvas> directamente ao DOM (dentro do container) em vez de
	 * depender do ref interno do react-chartjs-2, cuja API varia entre versoes.
	 * canvas.toDataURL() e suficiente para a imagem do PDF.
	 */
	const getCanvas = () => (
		containerRef.current ? containerRef.current.querySelector('canvas') : null
	);

	// entrada de exportacao sempre actualizada (o canvas e lido no momento)
	entryRef.current = { title, subtitle, period: periodForFile, legend: buildLegend() };

	useEffect(() => {
		registerChart(chartKey, () => ({
			...entryRef.current,
			canvas: getCanvas()
		}));
		return () => registerChart(chartKey, null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chartKey]);

	const _exportPdf = () => {
		ExportChartToPdf({
			...entryRef.current,
			canvas: getCanvas()
		});
	};

	return (
		<Card {...rest} className={clsx(classes.root, className)}>
			<CardHeader
				className={classes.header}
				title={title}
				action={(
					<span className={classes.headerAction}>
						{!isDual && kpi && kpi.unit
							? <Typography className={classes.unit}>{kpi.unit}</Typography>
							: null}
						<Tooltip title="Export chart to PDF">
							<IconButton size="small" onClick={_exportPdf} aria-label="Export chart to PDF">
								<PictureAsPdfIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</span>
				)}
				titleTypographyProps={{ variant: 'h6', className: classes.title }}
			/>
			<Divider />
			<CardContent className={classes.content}>
				<div className={classes.chart} ref={containerRef}>
					<Line data={data} options={options} plugins={[WorkDayLinePlugin]} />
				</div>
			</CardContent>
		</Card>
	);
};

KPIsOverviewChart.propTypes = {
	className: PropTypes.string,
	kpi: PropTypes.object,
	series: PropTypes.array.isRequired,
	kpi2: PropTypes.object,
	series2: PropTypes.array,
	buckets: PropTypes.array.isRequired,
	granularity: PropTypes.string,
	workDay: PropTypes.any,
	shownGroupings: PropTypes.array,
	colorMap: PropTypes.object,
	chartKey: PropTypes.string
};

KPIsOverviewChart.defaultProps = {
	shownGroupings: [],
	colorMap: null,
	kpi2: null,
	series2: null
};

export default KPIsOverviewChart;